import { useState, type ReactNode } from 'react'
import { ChevronRight } from 'lucide-react'
import {
  tagHistorizationAppliedParts,
  type InFlightAgentActionProposal,
  type InFlightTagHistorizationProposal,
  type TagHistorizationTag
} from '@axilon-apps/domains-ontology-ui-state'

import {
  registerAgentActionView,
  type AgentActionViewCapability
} from '../../AgentActionApproval/views/viewRegistry'
import { cn } from '../../../lib/utils'
import { ACQUISITION_HISTORY_WARNING } from '../../../pages/TagsPage/acquisition-source'

const VISIBLE_TAG_ROWS = 20

const RESOLVING_BLOCKED_REASON = 'Waiting for the agent to resolve the selection.'
const ENGINEER_REQUIRED_MESSAGE =
  'An engineer needs to approve this change; you lack the permissions it requires.'

const BADGE_BY_OPERATION: Record<InFlightTagHistorizationProposal['operation'], string> = {
  enable: 'history on',
  disable: 'history off',
  change_source: 'history source'
}

const CREDENTIAL_POSTURE_COPY: Record<string, string> = {
  saved_credentials: 'Connects with the credentials saved for this endpoint.',
  anonymous: 'Connects anonymously; this endpoint allows anonymous access.',
  existing_configuration: "Uses this endpoint's existing connection settings.",
  new_anonymous:
    'Creates a new anonymous connection; an admin can add credentials on the Tags page if the endpoint needs them.'
}

function isTagHistorization(
  proposal: InFlightAgentActionProposal
): proposal is InFlightTagHistorizationProposal {
  return proposal.kind === 'tag_historization'
}

function modeLabel(scrapeMode: string): string {
  if (scrapeMode === 'subscribe') return 'OPC subscribe'
  if (scrapeMode === 'poll') return 'WebDev poll'
  return scrapeMode || 'unknown'
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="break-words px-1">
      <span className="text-muted-foreground">{label}:</span> {value}
    </div>
  )
}

function Disclosure({
  label,
  count,
  children
}: {
  label: string
  count: number
  children: ReactNode
}) {
  const [open, setOpen] = useState(false)
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full cursor-pointer items-center gap-1.5 rounded-md px-1 py-1 text-left transition-colors duration-200 ease-out hover:bg-muted/60"
      >
        <ChevronRight
          aria-hidden
          className={cn(
            'size-3 shrink-0 text-muted-foreground transition-transform duration-200 ease-out motion-reduce:transition-none',
            open && 'rotate-90'
          )}
        />
        <span className="min-w-0 flex-1 truncate">{label}</span>
        <span className="shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-10 leading-none tabular-nums text-muted-foreground">
          {count}
        </span>
      </button>
      <div
        data-open={open}
        className="grid grid-rows-[0fr] transition-[grid-template-rows] duration-200 ease-out data-[open=true]:grid-rows-[1fr]"
      >
        <div className="min-h-0 overflow-hidden">
          <ul className="mt-0.5 mb-1 ml-2.5 grid gap-0.5 border-l border-line/70 pl-3 text-10">
            {children}
          </ul>
        </div>
      </div>
    </div>
  )
}

function MoreRow({ hidden }: { hidden: number }) {
  if (hidden <= 0) return null
  return <li className="text-muted-foreground">+{hidden} more</li>
}

function TagList({ label, tags }: { label: string; tags: TagHistorizationTag[] }) {
  if (tags.length === 0) return null
  return (
    <Disclosure label={label} count={tags.length}>
      {tags.slice(0, VISIBLE_TAG_ROWS).map((tag) => (
        <li key={tag.nodeId} className="break-all font-mono text-foreground/80">
          {tag.tagPath || tag.nodeId}
        </li>
      ))}
      <MoreRow hidden={tags.length - VISIBLE_TAG_ROWS} />
    </Disclosure>
  )
}

function ExcludedList({ proposal }: { proposal: InFlightTagHistorizationProposal }) {
  if (proposal.excluded.length === 0) return null
  return (
    <Disclosure label="Left out" count={proposal.excluded.length}>
      {proposal.excluded.slice(0, VISIBLE_TAG_ROWS).map((tag) => (
        <li key={tag.nodeId || tag.tagPath} className="break-words">
          <span className="font-mono text-foreground/80">{tag.tagPath || tag.nodeId}</span>
          {tag.reason ? <span className="text-muted-foreground"> — {tag.reason}</span> : null}
        </li>
      ))}
      <MoreRow hidden={proposal.excluded.length - VISIBLE_TAG_ROWS} />
    </Disclosure>
  )
}

function Note({ children }: { children: string }) {
  return <p className="px-1 text-10 leading-relaxed text-muted-foreground">{children}</p>
}

function EnableDetail({ proposal }: { proposal: InFlightTagHistorizationProposal }) {
  const destination = proposal.registerDestination
  return (
    <>
      <TagList label="Register and start recording" tags={proposal.toRegister} />
      {destination && proposal.toRegister.length > 0 && (
        <DetailRow
          label={destination.transport === 'opc' ? 'OPC endpoint' : 'WebDev gateway'}
          value={destination.url}
        />
      )}
      <TagList label="Resume recording" tags={proposal.toEnable} />
      {proposal.unchangedCount > 0 && (
        <DetailRow label="Already recording" value={String(proposal.unchangedCount)} />
      )}
    </>
  )
}

function DisableDetail({ proposal }: { proposal: InFlightTagHistorizationProposal }) {
  return (
    <>
      <TagList label="Stop recording" tags={proposal.toDisable} />
      {proposal.unchangedCount > 0 && (
        <DetailRow label="Already off or not registered" value={String(proposal.unchangedCount)} />
      )}
      <Note>Recorded history is kept and live reads still work.</Note>
    </>
  )
}

function ChangeSourceDetail({ proposal }: { proposal: InFlightTagHistorizationProposal }) {
  const { source, target } = proposal
  const posture = target?.credentialPosture
    ? CREDENTIAL_POSTURE_COPY[target.credentialPosture]
    : undefined
  return (
    <>
      {source && (
        <DetailRow label="From" value={`${modeLabel(source.scrapeMode)} · ${source.sourceUrl}`} />
      )}
      {target && (
        <DetailRow
          label="To"
          value={`${modeLabel(target.scrapeMode)} · ${target.destinationUrl}`}
        />
      )}
      {posture && <DetailRow label="Connection" value={posture} />}
      <TagList label="Switch source" tags={proposal.convertTags} />
      <Note>{`Live reads and new history both move to the new source. ${ACQUISITION_HISTORY_WARNING}`}</Note>
    </>
  )
}

function TagHistorizationDetail({ proposal }: { proposal: InFlightTagHistorizationProposal }) {
  return (
    <div className="rounded-xl border border-line/70 bg-muted/30 p-2 text-11 text-foreground">
      <div className="grid gap-1">
        {proposal.resolving ? (
          <div className="px-1 text-muted-foreground">Resolving the selection…</div>
        ) : proposal.operation === 'enable' ? (
          <EnableDetail proposal={proposal} />
        ) : proposal.operation === 'disable' ? (
          <DisableDetail proposal={proposal} />
        ) : (
          <ChangeSourceDetail proposal={proposal} />
        )}
        {!proposal.resolving && <ExcludedList proposal={proposal} />}
        <DetailRow label="Reason" value={proposal.reason} />
      </div>
    </div>
  )
}

function appliedCopy(proposal: InFlightTagHistorizationProposal): string {
  const result = proposal.appliedResult
  if (!result) return 'Applied.'
  const parts = tagHistorizationAppliedParts(result)
  return parts.length > 0 ? `Applied — ${parts.join(', ')}.` : 'Applied — nothing needed changing.'
}

function TagHistorizationSettled({ proposal }: { proposal: InFlightTagHistorizationProposal }) {
  const terminal = proposal.terminalState
  if (!terminal) return null
  if (terminal.kind === 'apply_failed') {
    return (
      <p className="px-1 text-10 text-destructive">Tag history change failed — {terminal.error}</p>
    )
  }
  if (terminal.kind === 'rejected') {
    const reason = proposal.rejectionReason?.replace(/[.\s]+$/, '')
    return (
      <p className="px-1 text-10 text-muted-foreground">
        Rejected{reason ? ` — ${reason}` : ''}. Nothing changed.
      </p>
    )
  }
  const failed = proposal.appliedResult?.failed ?? []
  return (
    <div className="grid gap-0.5 px-1 text-10">
      <p className="text-emerald-600 dark:text-emerald-400">{appliedCopy(proposal)}</p>
      {failed.length > 0 && (
        <ul className="text-destructive">
          {failed.slice(0, VISIBLE_TAG_ROWS).map((failure, index) => (
            <li key={`${failure.tagPath}-${index}`} className="break-words">
              {failure.tagPath || 'Tag'} failed — {failure.reason}
            </li>
          ))}
          {failed.length > VISIBLE_TAG_ROWS && (
            <li>+{failed.length - VISIBLE_TAG_ROWS} more failed</li>
          )}
        </ul>
      )}
    </div>
  )
}

export const tagHistorizationViewCapability: AgentActionViewCapability = {
  kind: 'tag_historization',
  badgeLabel(proposal) {
    return isTagHistorization(proposal) ? BADGE_BY_OPERATION[proposal.operation] : 'history'
  },
  applyBlockedReason(proposal, permissions) {
    if (!isTagHistorization(proposal)) return null
    if (proposal.resolving) return RESOLVING_BLOCKED_REASON
    const missing = proposal.requiredPermissions.some(
      (permission) => !permissions?.includes(permission)
    )
    return missing ? ENGINEER_REQUIRED_MESSAGE : null
  },
  renderDetail(proposal) {
    if (!isTagHistorization(proposal)) return null
    return <TagHistorizationDetail proposal={proposal} />
  },
  renderSettledSummary(proposal) {
    if (!isTagHistorization(proposal)) return null
    return <TagHistorizationSettled proposal={proposal} />
  }
}

registerAgentActionView(tagHistorizationViewCapability)
